import _ from 'underscore';

import EntityStore from 'stores/EntityStore';

import {TreeItem, TreeItemState, TreeItemStateMap} from './SelectTreeConstants';

/** The items rendered directly under an expanded item, in display order. */
export function getDisplayedChildItems(
  itemState: TreeItemState,
  siblingSortFn?: (items: Array<TreeItem>) => Array<TreeItem>
): Array<TreeItem> {
  const {childItems, baseLeafItems, leafItems} = itemState;
  // Keep base leaf items at the top unless it is loaded into leafItems
  const firstLeafItems = baseLeafItems.filter((item) => !_.findWhere(leafItems, {id: item.id}));
  const unsortedItems = [...childItems, ...firstLeafItems, ...leafItems];
  return siblingSortFn?.(unsortedItems) ?? unsortedItems;
}

export function getAllDisplayedIds(
  itemStateMap: TreeItemStateMap,
  rootItems: Array<TreeItem>,
  siblingSortFn?: (items: Array<TreeItem>) => Array<TreeItem>
): Array<string> {
  const allDisplayedIds: Array<string> = [];
  rootItems.forEach((rootItem) => {
    addDisplayedIds(allDisplayedIds, rootItem.id, itemStateMap, siblingSortFn);
  });
  return allDisplayedIds;
}

function addDisplayedIds(
  idList: Array<string>,
  itemId: string,
  itemStateMap: TreeItemStateMap,
  siblingSortFn: ((items: Array<TreeItem>) => Array<TreeItem>) | undefined
): void {
  idList.push(itemId);
  // Leaf items have no item state.
  const itemState = itemStateMap[itemId] as TreeItemState | undefined;
  if (itemState?.isExpanded) {
    getDisplayedChildItems(itemState, siblingSortFn).forEach((item) => {
      addDisplayedIds(idList, item.id, itemStateMap, siblingSortFn);
    });
  }
}

export function findShiftSelectedIds(
  nextSelectedIds: Array<string>,
  allDisplayedIds: Array<string>,
  currentSelectedId: string,
  previousSelectedIds: Array<string>,
  previousClickedIds: Array<string>
): Array<string> {
  const isUnclick = previousSelectedIds.includes(currentSelectedId);
  const [first, last] = getShiftClickEndpoints(
    nextSelectedIds,
    allDisplayedIds,
    currentSelectedId,
    previousClickedIds
  );
  if (first === last) {
    return [first];
  }

  let inBetween = false;
  let shiftSelectedIds = [...nextSelectedIds];
  if (isUnclick) {
    shiftSelectedIds.push(currentSelectedId); // currentSelectedId stays selected upon shift-click
    allDisplayedIds.forEach((currentId) => {
      if (currentId === first || currentId === last) {
        inBetween = !inBetween;
        if (currentId !== currentSelectedId && shiftSelectedIds.includes(currentId)) {
          shiftSelectedIds = shiftSelectedIds.filter((id) => id !== currentId);
        }
      } else if (inBetween && shiftSelectedIds.includes(currentId)) {
        shiftSelectedIds = shiftSelectedIds.filter((id) => id !== currentId);
      }
    });
  } else {
    allDisplayedIds.forEach((currentId) => {
      if (currentId === first || currentId === last) {
        inBetween = !inBetween;
        if (!shiftSelectedIds.includes(currentId)) {
          shiftSelectedIds.push(currentId);
        }
      } else if (inBetween && !shiftSelectedIds.includes(currentId)) {
        shiftSelectedIds.push(currentId);
      }
    });
  }
  return shiftSelectedIds;
}

function getShiftClickEndpoints(
  selectedIds: Array<string>,
  allDisplayedIds: Array<string>,
  currentSelectedId: string,
  previousClickedIds: Array<string>
): Array<string> {
  let previousSelectedId: string;
  if (previousClickedIds.length > 0) {
    let foundValidPrevClick = false;
    let prevClickNowSelected: string = '';
    let idx = previousClickedIds.length - 1;
    while (!foundValidPrevClick && idx >= 0) {
      prevClickNowSelected = previousClickedIds[idx];
      if (selectedIds.includes(prevClickNowSelected)) {
        foundValidPrevClick = true;
      } else {
        idx -= 1;
      }
    }
    previousSelectedId = !foundValidPrevClick ? currentSelectedId : prevClickNowSelected;
  } else {
    previousSelectedId = currentSelectedId;
  }

  let found = false;
  let first: string = '';
  let last: string = '';
  allDisplayedIds.forEach((currentId) => {
    if (currentId === previousSelectedId) {
      if (!found) {
        first = previousSelectedId;
        found = true;
      } else {
        last = previousSelectedId;
      }
    }
    // account for when currentSelectedId === previousSelectedId
    if (currentId === currentSelectedId) {
      if (!found) {
        first = currentSelectedId;
        found = true;
      } else {
        last = currentSelectedId;
      }
    }
  });
  return [first, last];
}

export function sortedRootItems(rootItems: Array<TreeItem>): Array<TreeItem> {
  // Sort by username + folder name. Push current user's folders to top
  // The paginated folder picker gets this order from the server (_order_top_level_folders in
  // benchling/nav/api/folders.py), so keep the two in sync.
  return _.chain(rootItems)
    .sortBy((rootItem) => rootItem.name.toLowerCase())
    .sortBy((rootItem) => (rootItem.user !== EntityStore.currentUser()?.handle ? rootItem.user : ''))
    .sortBy((rootItem) => (rootItem.forceToTop ? 0 : 1))
    .value();
}
